import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Button } from "@/shared/ui/button";
import type { FileItem } from "../../types";
import type { DisplayAttribute } from "@/features/admin/panels/categories/hooks/useCategoryAttributesDisplay";
import { AttributeField } from "../upload-modal/UploadModalAttributeField";
import { bulkUpdateFileAttributes } from "@/services/api/files";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface BulkEditAttributesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedFiles: FileItem[];
  categoryId: string | number;
  categoryAttributes: DisplayAttribute[];
  isLoading?: boolean;
  onSuccess?: () => void;
}

export function BulkEditAttributesDialog({
  open,
  onOpenChange,
  selectedFiles,
  categoryId,
  categoryAttributes,
  isLoading: attributesLoading,
  onSuccess,
}: BulkEditAttributesDialogProps) {
  const [attributeValues, setAttributeValues] = useState<
    Record<string | number, string | number | undefined>
  >({});
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: bulkUpdateFileAttributes,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["gallery"] });
      toast.success("Attributes updated", {
        description: `Attributes have been updated for ${selectedFiles.length} file(s).`,
      });
      onOpenChange(false);
      setAttributeValues({});
      onSuccess?.();
    },
    onError: (error: Error) => {
      toast.error("Failed to update attributes", {
        description:
          error.message || "An error occurred while updating attributes.",
      });
    },
  });

  useEffect(() => {
    if (!open) {
      setAttributeValues({});
    }
  }, [open]);

  const handleAttributeChange = (
    attributeId: string | number,
    value: string | number | undefined
  ) => {
    setAttributeValues((prev) => ({
      ...prev,
      [attributeId]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const attributeValuesArray = Object.entries(attributeValues)
      .filter(([_, value]) => value !== undefined && value !== "")
      .map(([attributeId, value]) => ({
        attributeId: Number(attributeId),
        value: String(value),
      }));

    if (attributeValuesArray.length === 0) {
      toast.error("No attributes selected", {
        description: "Please select at least one attribute value to update.",
      });
      return;
    }

    mutation.mutate({
      categoryId: Number(categoryId),
      fileIds: selectedFiles.map((f) => f.id),
      attributeValues: attributeValuesArray,
    });
  };

  const requiredAttributes = categoryAttributes.filter(
    (attr) => attr.isRequired
  );
  const optionalAttributes = categoryAttributes.filter(
    (attr) => !attr.isRequired
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Attributes</DialogTitle>
          <DialogDescription>
            Update attributes for {selectedFiles.length} selected file(s).
            Changes will affect all selected files.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {attributesLoading ? (
            <div className="py-8 text-center text-sm text-ink-500">
              Loading attributes...
            </div>
          ) : (
            <div className="space-y-4">
              {requiredAttributes.length > 0 && (
                <div className="space-y-4">
                  {requiredAttributes.map((attr) => (
                    <AttributeField
                      key={attr.id}
                      attribute={attr}
                      value={attributeValues[attr.id]}
                      isRequired
                      onChange={(value) =>
                        handleAttributeChange(attr.id, value)
                      }
                    />
                  ))}
                </div>
              )}
              {optionalAttributes.length > 0 && (
                <div className="space-y-4 pt-4 border-t border-grid-200">
                  {optionalAttributes.map((attr) => (
                    <AttributeField
                      key={attr.id}
                      attribute={attr}
                      value={attributeValues[attr.id]}
                      isRequired={attr.isRequired}
                      onChange={(value) =>
                        handleAttributeChange(attr.id, value)
                      }
                    />
                  ))}
                </div>
              )}
              {categoryAttributes.length === 0 && (
                <p className="text-sm text-ink-500">
                  No attributes configured for this category.
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || attributesLoading}
            >
              {mutation.isPending ? "Updating..." : "Update Attributes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
